import React, { useEffect, useState } from "react";
import {
  Container, Card, CardBody, Button, Table, Input, Row, Col, Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from "reactstrap";
import { Formik, FieldArray } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { dalaActions } from "../../sagas/dalaSlice";
import { productActions } from "../../sagas/productSlice";
import { useTranslation } from "react-i18next";
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaAngleLeft, FaAngleRight, FaBox, FaBoxes, FaTag } from "react-icons/fa";
import { formatPrice } from "../../helpers/utility";
import { transporteurActions } from "../../sagas/transporteurSlice";
import { commercialActions } from "../../sagas/commercialSlice";
import { clientActions } from "../../sagas/clientSlice";
import { devisActions } from "../../sagas/devisSlice";
import { commandeActions } from "../../sagas/commandeSlice";
import { entrepotActions } from "../../sagas/entrepotSlice";
import Select from "react-select";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./DalaFormPage.css";

const articles = [
  { id: 1, nom: "Poutrelle A", code: "PA", prix: 100 },
  { id: 2, nom: "Poutrelle B", code: "PB", prix: 120 },
];

// Function to calculate result from input row
const calculateRow = (r) => {
  if (r.largeur > 0 && r.longeur > 0) {
    const largeur = Number(r.largeur || 0);
    const longeur = Number(r.longeur || 0);
    const largeurML = largeur + 0.2;

    let nbP = Number((longeur / 0.6).toFixed(6));
    const decimalPart = Number((nbP % 1).toFixed(6));
    nbP = decimalPart < 0.83 ? Math.trunc(nbP) : Math.round(nbP);
    if (r.depart) nbP += 1;

    const surface = largeur * longeur;
    const nbHourdis = (surface * 8.4);

    const article = articles.find(a => a.id === r.articleId) || { prix: 0 };
    const prixUnitaire = Number(r.prixUnitaire ?? article.prix);
    const montantLigne = nbP * prixUnitaire;

    return { ...r, largeurML, nbP, surface, nbHourdis, article, montantLigne, prixUnitaire };
  } else {
    const article = articles.find(a => a.id === r.articleId) || { prix: 0 };
    const prixUnitaire = Number(r.prixUnitaire ?? article.prix);
    const quantite = Number(r.quantite || 0);
    const montantLigne = quantite * prixUnitaire;

    return { ...r, nbP: quantite, montantLigne, prixUnitaire, article };
  }
};

export default function DalaFormPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const dalaId = params.id;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [chooseProductModal, setChooseProductModal] = useState(false);

  const { products, loading, totalPages, totalElements, pageSize } = useSelector((state) => state.products);
  const clients = useSelector(state => state.clients.clients);
  const { transporteurs } = useSelector(state => state.transporteurs);
  const { entrepots } = useSelector(state => state.entrepots);
  const { list: commercialList, loading: commercialLoading } = useSelector((state) => state.commercials);
  const { dala: currentDala } = useSelector(state => state.dala);
  const { company } = useSelector(state => state.auth);

  const { t } = useTranslation('translation');
  const toggleChooseProductModal = () => setChooseProductModal(!chooseProductModal);

  const today = new Date();
  const date =
    today.getFullYear() +
    "-" +
    String(today.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(today.getDate()).padStart(2, "0");

  useEffect(() => {
    dispatch(transporteurActions.list());
    dispatch(entrepotActions.list());
    dispatch(commercialActions.list({ query: '' }));
    dispatch(clientActions.search({ query: "" }));

    if (dalaId) {
      dispatch(dalaActions.getById({ id: dalaId }));
    } else {
      // When switching from edit to new route, clear previous form entity from store.
      dispatch(dalaActions.clearFormState());
      setCurrentPage(1);
      setSearchTerm('');
      setCurrentRowIndex(null);
      setChooseProductModal(false);
    }

    return () => {
      // Ensure form-related Redux state is cleared when leaving this page.
      dispatch(dalaActions.clearFormState());
    };
  }, [dispatch, dalaId])

  useEffect(() => {
    dispatch(productActions.findAll({
      search: searchTerm,
      page: currentPage - 1,
      size: 20,
    }));

  }, [currentPage, searchTerm]);


  const mapDalaToForm = (dala) => ({
    id: dala?.id ?? null,
    clientId: dala?.clientId ?? null,
    transporteurId: dala?.transporteurId ?? null,
    commercialId: dala?.commercialId ?? null,
    date: dala?.date ?? date,
    details: dala?.details?.map(d => ({
      id: d.id,
      longeur: d.longeur,
      largeur: d.largeur,
      depart: d.depart,
      articleId: d.articleId,
      articleNom: d.articleNom,
      prixUnitaire: d.prixUnitaire,
      quantite: d.quantite
    })) ?? [],
  });

  const openProductModalForRow = (index) => {
    setCurrentRowIndex(index);
    setChooseProductModal(true);
  };

  const handleProductSelect = (product, setFieldValue) => {
    if (currentRowIndex !== null) {
      setFieldValue(`details.${currentRowIndex}.articleId`, product.id);
      setFieldValue(`details.${currentRowIndex}.articleNom`, product.libelle);
      setFieldValue(`details.${currentRowIndex}.prixUnitaire`, product.prixVenteTTC1 ?? product.prix);
      setChooseProductModal(false);
      setCurrentRowIndex(null);
    }
  };

  const TableView = ({ setFieldValue, handleProductSelect }) => (
    <div className="cart-container" style={{ height: '300px', overflowY: 'auto' }}>
      <Table hover bordered className="align-middle mb-0">
        <thead className="bg-light">
          <tr>
            <th className="align-middle">
              <FaBox className="me-2 text-primary" size={12} />
              {t('product.description')}
            </th>
            <th className="align-middle text-end">
              <FaTag className="me-2 text-primary" size={12} />
              {t('product.salePrice')}
            </th>
            <th className="align-middle text-center">
              <FaBoxes className="me-2 text-primary" size={12} />
              {t('product.stockPhysique')}
            </th>
          </tr>
        </thead>
        <tbody>
          {!products || products.length === 0 ? (
            <tr>
              <td colSpan="3" className="text-center py-4">
                <div className="text-muted">{t('product.noProducts')}</div>
              </td>
            </tr>
          ) : (
            products.map((product) => (
              <tr
                key={product.id}
                onClick={() => handleProductSelect(product, setFieldValue)}
                style={{ cursor: 'pointer' }}
              >
                <td className="align-middle">{product.libelle}</td>
                <td className="align-middle text-end">{formatPrice(product.prixVenteTTC1)}</td>
                <td className="align-middle text-center">{product.stockPhysique}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );

  const commercialOptions = commercialList?.map(com => ({
    value: com.id,
    label: com.fullName,
  })) ?? [];

  const transporteurOptions = transporteurs?.map(transporteur => ({
    value: transporteur.id,
    label: transporteur.code + ' - ' + transporteur.libelle,
  })) ?? [];

  const clientOptions = clients?.map(client => ({
    solde: client.solde,
    totalDebit: client.totalDebit,
    totalCredit: client.totalCredit,
    chantiers: client.chantiers,
    value: client.id,
    label: client.typeClient === 'PARTICULIER'
      ? `${client.prenom} ${client.nom}`
      : client.entreprise,
  })) ?? [];


  const DisplayToggle = () => (
    <div className="d-flex justify-content-between align-items-center mb-3">
      <div className="btn-group">

      </div>

      <div className="d-flex align-items-center gap-3">
        <small className="text-muted">
          {t('product.showing', {
            start: (currentPage - 1) * pageSize + 1,
            end: Math.min(currentPage * pageSize, totalElements),
            total: totalElements,
            currentPage: currentPage,
            totalPages: totalPages
          })}
        </small>

        <div className="d-flex align-items-center gap-2">
          <Button color="light" size="sm" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
            <FaAngleDoubleLeft />
          </Button>
          <Button color="light" size="sm" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
            <FaAngleLeft />
          </Button>

          <span className="mx-2">
            {currentPage} / {totalPages}
          </span>

          <Button color="light" size="sm" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>
            <FaAngleRight />
          </Button>
          <Button color="light" size="sm" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
            <FaAngleDoubleRight />
          </Button>
        </div>
      </div>
    </div>
  );


  const handleModalSearchChange = (e) => {
    setCurrentPage(1);
    setSearchTerm(e.target.value);
  };

  const gridFields = ["longeur", "largeur", "quantite", "prixUnitaire"];

  const preventWheelChange = (event) => {
    event.preventDefault();
  };

  const focusGridInput = (rowIndex, fieldName) => {
    if (rowIndex < 0) return;
    const el = document.querySelector(
      `input[data-grid-row="${rowIndex}"][data-grid-field="${fieldName}"]`
    );
    if (el) {
      el.focus();
      el.select?.();
    }
  };

  const handleGridKeyDown = (event, rowIndex, fieldName, rowCount) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
      const nextRow = event.key === "ArrowUp" ? rowIndex - 1 : rowIndex + 1;
      if (nextRow >= 0 && nextRow < rowCount) {
        focusGridInput(nextRow, fieldName);
      }
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const currentFieldIndex = gridFields.indexOf(fieldName);
      if (currentFieldIndex === -1) return;

      const isLastField = currentFieldIndex === gridFields.length - 1;
      const nextField = isLastField ? gridFields[0] : gridFields[currentFieldIndex + 1];
      const nextRow = isLastField ? rowIndex + 1 : rowIndex;

      if (nextRow < rowCount) {
        focusGridInput(nextRow, nextField);
      }
      return;
    }

    // Ignore non-data keys that typically cause unwanted numeric behavior
    if (event.key === "e" || event.key === "E") {
      event.preventDefault();
    }
  };

  const bindWheelBlock = (event) => {
    event.target.addEventListener("wheel", preventWheelChange, { passive: false });
  };

  const unbindWheelBlock = (event) => {
    event.target.removeEventListener("wheel", preventWheelChange);
  };

  const getDisplayName = (options, id) => {
    const match = options.find((option) => option.value === id);
    return match?.label || "-";
  };

  const toSalesLigne = (r) => {
    const qty = Number(r.nbP || 0);
    const puTTC = Number(r.prixUnitaire || 0);
    const tauxTVA = 0.2;
    const puHT = puTTC / (1 + tauxTVA);
    const montantHT = qty * puHT;
    const montantTTC = qty * puTTC;

    return {
      articleId: r.articleId,
      articleNom: r.articleNom || `Article ${r.articleId}`,
      quantite: Number(qty.toFixed(2)),
      unite: "U",
      tauxTVA,
      prixUnitaireHT: Number(puHT.toFixed(2)),
      prixUnitaireTTC: Number(puTTC.toFixed(2)),
      montantHT: Number(montantHT.toFixed(2)),
      montantTTC: Number(montantTTC.toFixed(2)),
    };
  };

  const buildSalesPayloadFromDala = (values) => {
    const calculatedRows = (values.details || []).map(calculateRow);
    const salesRows = calculatedRows
      .filter((r) => Number(r.nbP || 0) > 0 && Number(r.prixUnitaire || 0) >= 0 && r.articleId)
      .map(toSalesLigne);

    const montantTotalHT = salesRows.reduce((a, b) => a + Number(b.montantHT || 0), 0);
    const montantTotalTTC = salesRows.reduce((a, b) => a + Number(b.montantTTC || 0), 0);
    const montantTotalTaxe = montantTotalTTC - montantTotalHT;

    return {
      clientId: values.clientId || null,
      chantierId: null,
      lignes: salesRows,
      dateOperation: values.date,
      remarques: values.id ? `Converti depuis Dala #${values.id}` : "Converti depuis Dala",
      montantTotalHT: Number(montantTotalHT.toFixed(2)),
      montantTotalTTC: Number(montantTotalTTC.toFixed(2)),
      montantTotalTaxe: Number(montantTotalTaxe.toFixed(2)),
      modeReglement: null,
    };
  };

  const validateConversionPayload = (payload) => {
    if (!payload.clientId) {
      Swal.fire({
        icon: "warning",
        title: t("sale.errorTitle"),
        text: t("sale.clientRequired")
      });
      return false;
    }

    if (!payload.lignes || payload.lignes.length === 0) {
      Swal.fire({
        icon: "warning",
        title: t("sale.errorTitle"),
        text: "Aucune ligne valide (article + quantité) à convertir."
      });
      return false;
    }

    return true;
  };

  const handleConvertToDevis = (values) => {
    const payload = buildSalesPayloadFromDala(values);
    if (!validateConversionPayload(payload)) return;

    Swal.fire({
      icon: "question",
      title: "Convertir en devis",
      text: "Voulez-vous créer un devis à partir de ce Dala ?",
      showCancelButton: true,
      confirmButtonText: "Confirmer",
      cancelButtonText: t("common.cancel")
    }).then((result) => {
      if (!result.isConfirmed) return;

      dispatch(devisActions.add({
        data: {
          ...payload,
          validite: 30,
          statut: "EN_ATTENTE",
        },
        onSuccess: () => {
          Swal.fire({
            icon: "success",
            title: t("common.created"),
            text: "Devis créé avec succès."
          });
          navigate("/sales-module/quotes");
        }
      }));
    });
  };

  const handleConvertToCommande = async (values) => {
    const payload = buildSalesPayloadFromDala(values);
    if (!validateConversionPayload(payload)) return;

    const entrepotOptions = (entrepots || []).map((e) => ({
      id: String(e.id),
      text: `${e.code || ""} - ${e.libelle || ""}`.trim()
    }));

    if (!entrepotOptions.length) {
      Swal.fire({
        icon: "warning",
        title: "Entrepôt requis",
        text: "Aucun entrepôt disponible. Veuillez créer/configurer un entrepôt d'abord."
      });
      return;
    }

    const { value: entrepotId } = await Swal.fire({
      title: "Choisir un entrepôt",
      input: "select",
      inputOptions: Object.fromEntries(entrepotOptions.map((o) => [o.id, o.text])),
      inputPlaceholder: "Sélectionner un entrepôt",
      showCancelButton: true,
      confirmButtonText: "Convertir",
      cancelButtonText: t("common.cancel"),
      inputValidator: (value) => {
        if (!value) return "Veuillez sélectionner un entrepôt";
      }
    });

    if (!entrepotId) return;

    dispatch(commandeActions.add({
      data: {
        ...payload,
        status: "EN_ATTENTE",
        entrepotId: Number(entrepotId),
        transporteurId: values.transporteurId || null,
      },
      onSuccess: () => {
        Swal.fire({
          icon: "success",
          title: t("common.created"),
          text: "Bon de commande créé avec succès."
        });
        navigate("/sales-module/orders");
      }
    }));
  };

  const generateDalaPdf = ({
    values,
    calculatedRows,
    totals
  }) => {
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 14;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.text((company?.name || "MA SOCIETE").toUpperCase(), pageWidth / 2, 14, { align: "center" });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(company.subtitle, pageWidth / 2, 20, { align: "center" });
    doc.line(margin, 24, pageWidth - margin, 24);

    const infoRows = [
      ["Date", values.date || "-"],
      ["Client", getDisplayName(clientOptions, values.clientId)],
      ["Transporteur", getDisplayName(transporteurOptions, values.transporteurId)],
      ["Commercial", getDisplayName(commercialOptions, values.commercialId)],
    ];
    const tableMargin = { left: margin, right: margin, bottom: 22 };

    autoTable(doc, {
      startY: 28,
      head: [["Champ", "Valeur"]],
      body: infoRows,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const inputRows = (values.details || [])
      .filter((r) => Number(r.longeur) > 0 && Number(r.largeur) > 0)
      .map((r, i) => ([
        i + 1,
        Number(r.longeur || 0).toFixed(2),
        Number(r.largeur || 0).toFixed(2),
        r.depart ? "Oui" : "Non",
      ]));

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["No", "Longueur", "Largeur", "Depart"]],
      body: inputRows.length ? inputRows : [["-", "-", "-", "-"]],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [250, 250, 250], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const detailsRows = calculatedRows.map((r, i) => ([
      i + 1,
      r.articleNom || "-",
      Number(r.nbP || 0).toFixed(2),
      r.largeurML ? Number(r.largeurML).toFixed(2) : "-",
      Number(r.prixUnitaire || 0).toFixed(2),
      Number(r.montantLigne || 0).toFixed(2),
    ]));

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["N°", "Article", "Quantité", "ML", "PU", "Montant Ligne"]],
      body: detailsRows.length ? detailsRows : [["-", "Aucune ligne", "-", "-", "-", "-"]],
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [241, 245, 249], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const totalsRows = [
      ["Total Surface (m²)", Number(totals.totalSurface || 0).toFixed(2)],
      ["Total ML", Number(totals.totalML || 0).toFixed(2)],
      ["Nombre PTS", Number(totals.nombrePTS || 0).toFixed(0)],
      ["Nombre Hourdis", Number(totals.totalHourdis || 0).toFixed(0)],
      ["Nombre Poutrelle", Number(totals.totalPoutrelle || 0).toFixed(2)],
      ["Prix m²", Number(totals.prixMetreCarre || 0).toFixed(2)],
      ["Total", Number(totals.totalMontant || 0).toFixed(2)],
    ];

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      head: [["Résumé", "Valeur"]],
      body: totalsRows,
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [163, 240, 183], textColor: [15, 23, 42] },
      margin: tableMargin,
      theme: "grid",
    });

    const footerTitle = [company?.name, company?.subtitle].filter(Boolean).join(" - ");
    const line1 = [
      company?.address || null,
      [company?.phone1, company?.phone2].filter(Boolean).join(" / ") || null,
      company?.email || null,
    ].filter(Boolean).join(" | ");
    const line2 = [
      company?.identificationFiscale ? `IF: ${company.identificationFiscale}` : null,
      company?.ice ? `ICE: ${company.ice}` : null,
      company?.rc ? `RC: ${company.rc}` : null,
      company?.patente ? `Patente: ${company.patente}` : null,
      company?.cnss ? `CNSS: ${company.cnss}` : null,
    ].filter(Boolean).join(" | ");

    const pageCount = doc.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.getHeight();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      doc.setDrawColor(180);
      doc.line(margin, pageHeight - 16, pageWidth - margin, pageHeight - 16);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(80);
      if (footerTitle) doc.text(footerTitle, pageWidth / 2, pageHeight - 14, { align: "center" });
      if (line1) doc.text(line1, pageWidth / 2, pageHeight - 10, { align: "center" });
      if (line2) doc.text(line2, pageWidth / 2, pageHeight - 6, { align: "center" });
      doc.text(`Page ${page} / ${pageCount}`, pageWidth - margin, pageHeight - 3, { align: "right" });
    }

    const pdfBlobUrl = doc.output("bloburl");
    window.open(pdfBlobUrl, "_blank");
  };

  return (
    <Container fluid className="dala-form-page">
      {/* === Page Header === */}
      <Card className="mb-2 shadow-sm dala-header-card">
        <CardBody>
          <h2 className="mb-0">
            {dalaId ? "Modifier Dala" : "Nouveau Calcul de Plancher"}
          </h2>
          <small>Entrez les dimensions des surfaces et générez automatiquement les calculs de poutrelles et hourdis.</small>
        </CardBody>
      </Card>

      <Formik
        initialValues={currentDala ? mapDalaToForm(currentDala) : {
          clientId: null,
          transporteurId: null,
          commercialId: null,
          date: date,
          details: [],
          totalSurface: "",
          totalML: "",
          nombrePTS: "",
          nombreHourdis: "",
          nombrePourtelle: "",
          prixMetreCarre: "",
          total: ""
        }}
        enableReinitialize
        onSubmit={(values) => {

          if (!values.clientId) {
            Swal.fire({
              icon: 'warning',
              title: t('sale.errorTitle'),
              text: t('sale.clientRequired')
            });
            return;
          }

          if (!values.details.length > 0) {
            Swal.fire({
              icon: 'warning',
              title: t('sale.errorTitle'),
              text: t('sale.aucunDalaDetail')
            });
            return;
          }

          const calculatedRows = values.details.map(calculateRow);

          const filteredRows = calculatedRows.filter(
            r => Number(r.largeur) > 0 && Number(r.longeur) > 0
          );

          const totalSurface = filteredRows.reduce((a, b) => a + b.surface, 0);
          const totalML = filteredRows.reduce((a, b) => a + b.largeurML * b.nbP, 0);
          const totalHourdis = filteredRows.reduce((a, b) => a + b.nbHourdis, 0) + 5;
          const totalPoutrelle = filteredRows.reduce((a, b) => a + b.nbP, 0);
          const totalMontant = calculatedRows.reduce((a, b) => a + b.montantLigne, 0);
          const nombrePTS = Math.ceil(totalSurface / 10);
          const prixMetreCarre = totalSurface > 0 ? totalMontant / totalSurface : 0;

          const payload = {
            ...values,

            details: calculatedRows.map(r => ({
              id: r.id,
              articleId: r.articleId,
              articleNom: r.articleNom,
              articleCode: r.articleCode,
              longeur: r.longeur,
              largeur: r.largeur,
              depart: r.depart,
              largeurML: r.largeurML,
              nombrePoutrelle: r.nbP,
              nombreHourdis: r.nbHourdis,
              surface: r.surface,
              prixUnitaire: r.prixUnitaire,
              montantLigne: r.montantLigne,
              quantite: r.quantite,   // important for other articles
            })),

            totalSurface,
            totalML,
            nombrePTS,
            nombreHourdis: totalHourdis,
            nombrePourtelle: totalPoutrelle,
            prixMetreCarre,
            total: totalMontant,
          };

          if (values.id) {
            dispatch(dalaActions.updateDala({
              id: values.id,
              data: payload,
              onSuccess: () => {
                Swal.fire({
                  icon: 'success',
                  title: t('common.updated'),
                  text: 'Modifié avec succès',
                });
                navigate("/dala-module/list")
              }
            }));
          } else {
            dispatch(dalaActions.createDala({
              data: payload,
              onSuccess: () => {
                Swal.fire({
                  icon: 'success',
                  title: t('common.created'),
                  text: 'Créé avec succès',
                });
                navigate("/dala-module/list")
              }
            }));
          }
        }}
      >
        {({ values, setFieldValue, handleSubmit }) => {

          // Calculate results for all rows dynamically
          const calculatedRows = values.details.map(calculateRow);

          const totalSurface = calculatedRows.filter(r => Number(r.largeur) > 0 && Number(r.longeur) > 0).reduce((a, b) => a + b.surface, 0);
          const totalML = calculatedRows.filter(r => Number(r.largeur) > 0 && Number(r.longeur) > 0).reduce((a, b) => a + b.largeurML * b.nbP, 0);
          const totalHourdis = calculatedRows.filter(r => Number(r.largeur) > 0 && Number(r.longeur) > 0).reduce((a, b) => a + b.nbHourdis, 0) + 5;
          const totalPoutrelle = calculatedRows
            .filter(r => Number(r.largeur) > 0 && Number(r.longeur) > 0)
            .reduce((a, b) => a + b.nbP, 0);
          const totalMontant = calculatedRows.reduce((a, b) => a + b.montantLigne, 0);
          const nombrePTS = Math.ceil(totalSurface / 10);
          const prixMetreCarre = totalSurface > 0 ? (totalMontant / totalSurface).toFixed(2) : 0;

          return (
            <form onSubmit={handleSubmit} className="dala-form-shell">
              <Card className="shadow-sm mb-2 dala-top-card">
                <CardBody className="py-2">
                  <Row className="g-2 align-items-end">
                    <Col xl={3} md={6}>
                      <Label className="mb-1 small fw-semibold">Client</Label>
                      <Select
                        id="clientId"
                        options={clientOptions}
                        value={clientOptions.find(option => option.value === values?.clientId) || null}
                        onChange={e => setFieldValue("clientId", e?.value || null)}
                        placeholder={t('bonLivraison.selectClient')}
                        isSearchable
                        isClearable
                        className="dala-select"
                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </Col>
                    <Col xl={3} md={6}>
                      <Label className="mb-1 small fw-semibold">Transporteur</Label>
                      <Select
                        id="transporteurId"
                        options={transporteurOptions}
                        value={transporteurOptions.find(option => option.value === values?.transporteurId)}
                        onChange={(selectedOption) => setFieldValue("transporteurId", selectedOption?.value || null)}
                        placeholder={t('bonLivraison.selectTransporteur')}
                        isSearchable
                        className="dala-select"
                      />
                    </Col>
                    <Col xl={3} md={6}>
                      <Label className="mb-1 small fw-semibold">Commercial</Label>
                      <Select
                        id="commercialSelect"
                        options={commercialOptions}
                        placeholder={t('bonLivraison.selectClient')}
                        value={commercialOptions.find(option => option.value === values?.commercialId) || null}
                        onChange={(selectedOption) => setFieldValue("commercialId", selectedOption?.value || null)}
                        isSearchable
                        className="dala-select"
                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                        styles={{
                          menuPortal: base => ({ ...base, zIndex: 9999 })
                        }}
                      />
                    </Col>
                    <Col xl={3} md={6}>
                      <Label className="mb-1 small fw-semibold">Date</Label>
                      <Input type="date" bsSize="sm" value={values.date} onChange={e => setFieldValue("date", e.target.value)} />
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              <Card className="shadow-sm mb-2">
                <CardBody className="py-2">
                  <div className="dala-kpis">
                    <div><span>Total Surface</span><strong>{totalSurface.toFixed(2)} m²</strong></div>
                    <div><span>Total ML</span><strong>{totalML.toFixed(2)}</strong></div>
                    <div><span>Nombre PTS</span><strong>{nombrePTS}</strong></div>
                    <div><span>Nombre Hourdis</span><strong>{totalHourdis.toFixed(0)}</strong></div>
                    <div><span>Nombre Poutrelle</span><strong>{totalPoutrelle}</strong></div>
                    <div><span>Prix m²</span><strong>{prixMetreCarre}</strong></div>
                  </div>
                </CardBody>
              </Card>

              <div className="dala-grid">
                <Card className="shadow-sm dala-panel">
                  <CardBody className="p-0 d-flex flex-column">
                    <div className="dala-panel-header">
                      <h6 className="mb-0">Lignes de saisie</h6>
                      <FieldArray name="details">
                        {({ push }) => (
                          <Button
                            type="button"
                            color="primary"
                            size="sm"
                            onClick={() => push({ longeur: 0, largeur: 0, depart: false, articleId: null, prixUnitaire: 0, quantite: 0 })}
                          >
                            Ajouter Ligne
                          </Button>
                        )}
                      </FieldArray>
                    </div>
                    <div className="dala-table-wrap">
                      <FieldArray name="details">
                        {({ remove }) => (
                          <Table bordered size="sm" className="mb-0 dala-table">
                            <thead>
                              <tr>
                                <th style={{ width: "40px" }}>N°</th>
                                <th>Long.</th>
                                <th>Larg.</th>
                                <th style={{ width: "70px" }}>Départ</th>
                                <th style={{ width: "160px" }}>Article</th>
                                <th>Qté</th>
                                <th>PU</th>
                                <th style={{ width: "50px" }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {values.details.map((r, i) => (
                                <tr key={i}>
                                  <td>{i + 1}</td>
                                  <td>
                                    <Input
                                      bsSize="sm"
                                      type="number"
                                      data-grid-row={i}
                                      data-grid-field="longeur"
                                      value={r.longeur || ''}
                                      onChange={e => setFieldValue(`details.${i}.longeur`, e.target.value)}
                                      onKeyDown={(e) => handleGridKeyDown(e, i, "longeur", values.details.length)}
                                      onFocus={bindWheelBlock}
                                      onBlur={unbindWheelBlock}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      bsSize="sm"
                                      type="number"
                                      data-grid-row={i}
                                      data-grid-field="largeur"
                                      value={r.largeur || ''}
                                      onChange={e => setFieldValue(`details.${i}.largeur`, e.target.value)}
                                      onKeyDown={(e) => handleGridKeyDown(e, i, "largeur", values.details.length)}
                                      onFocus={bindWheelBlock}
                                      onBlur={unbindWheelBlock}
                                    />
                                  </td>
                                  <td className="text-center"><Input type="checkbox" checked={r.depart || false} onChange={e => setFieldValue(`details.${i}.depart`, e.target.checked)} /></td>
                                  <td>
                                    <Button type="button" size="sm" color="light" className="w-100 text-truncate border" onClick={() => openProductModalForRow(i)}>
                                      {r.articleId ? r.articleNom || 'Sélectionner' : 'Sélectionner'}
                                    </Button>
                                  </td>
                                  <td>
                                    <Input
                                      bsSize="sm"
                                      type="number"
                                      data-grid-row={i}
                                      data-grid-field="quantite"
                                      value={r.quantite || ''}
                                      onChange={e => setFieldValue(`details.${i}.quantite`, e.target.value)}
                                      onKeyDown={(e) => handleGridKeyDown(e, i, "quantite", values.details.length)}
                                      onFocus={bindWheelBlock}
                                      onBlur={unbindWheelBlock}
                                    />
                                  </td>
                                  <td>
                                    <Input
                                      bsSize="sm"
                                      type="number"
                                      data-grid-row={i}
                                      data-grid-field="prixUnitaire"
                                      value={r.prixUnitaire}
                                      onChange={e => setFieldValue(`details.${i}.prixUnitaire`, e.target.value)}
                                      onKeyDown={(e) => handleGridKeyDown(e, i, "prixUnitaire", values.details.length)}
                                      onFocus={bindWheelBlock}
                                      onBlur={unbindWheelBlock}
                                    />
                                  </td>
                                  <td><Button type="button" color="danger" size="sm" onClick={() => remove(i)}>X</Button></td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </FieldArray>
                    </div>
                  </CardBody>
                </Card>

                <Card className="shadow-sm dala-panel">
                  <CardBody className="p-0 d-flex flex-column">
                    <div className="dala-panel-header">
                      <h6 className="mb-0">Résultats</h6>
                    </div>
                    <div className="dala-table-wrap">
                      <Table bordered size="sm" className="mb-0 dala-table">
                        <thead>
                          <tr>
                            <th style={{ width: "40px" }}>N°</th>
                            <th>Article</th>
                            <th>Quantité</th>
                            <th>ML</th>
                            <th>PU</th>
                            <th>Montant Ligne</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calculatedRows.map((r, i) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{r.articleNom}</td>
                              <td>{r.nbP}</td>
                              <td>{r.largeurML ? r.largeurML.toFixed(2) : '-'}</td>
                              <td>{r.prixUnitaire}</td>
                              <td>{r.montantLigne.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr>
                            <td colSpan={2}><b>Total</b></td>
                            <td><b>{calculatedRows.reduce((a, b) => a + b.nbP, 0)}</b></td>
                            <td></td>
                            <td></td>
                            <td><b>{calculatedRows.reduce((a, b) => a + b.montantLigne, 0).toFixed(2)}</b></td>
                          </tr>
                        </tfoot>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              </div>

              <div className="dala-action-bar">
                <Button type="submit" color="success" size="sm">Enregistrer</Button>
                <Button type="button" color="info" size="sm" onClick={() => handleConvertToDevis(values)}>
                  Convertir en Devis
                </Button>
                <Button type="button" color="warning" size="sm" onClick={() => handleConvertToCommande(values)}>
                  Convertir en Bon de commande
                </Button>
                <Button
                  type="button"
                  color="primary"
                  size="sm"
                  onClick={() => {
                    const hasDetails = values.details && values.details.length > 0;
                    if (!hasDetails) {
                      Swal.fire({
                        icon: "warning",
                        title: t("sale.errorTitle"),
                        text: t("sale.aucunDalaDetail")
                      });
                      return;
                    }
                    generateDalaPdf({
                      values,
                      calculatedRows,
                      totals: {
                        totalSurface,
                        totalML,
                        nombrePTS,
                        totalHourdis,
                        totalPoutrelle,
                        prixMetreCarre,
                        totalMontant
                      }
                    });
                  }}
                >
                  Aperçu PDF
                </Button>
              </div>
              <Modal isOpen={chooseProductModal} toggle={toggleChooseProductModal} size="xl">
                <ModalHeader toggle={toggleChooseProductModal}>
                  {t('product.chooseProduct')}
                </ModalHeader>
                <ModalBody>
                  <Row>
                    <Col md={12}>
                      <Input
                        type="text"
                        placeholder={t('product.searchPlaceholder')}
                        value={searchTerm}
                        onChange={handleModalSearchChange}
                        className="me-2" // Added margin to the right
                      />
                    </Col>
                  </Row>
                  <DisplayToggle />
                  <TableView
                    setFieldValue={setFieldValue}      // Pass Formik function
                    handleProductSelect={handleProductSelect} // Pass the function
                  />
                </ModalBody>
                <ModalFooter>
                  <Button color="secondary" onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1);
                    toggleChooseProductModal()
                  }}>
                    {t('common.cancel')}
                  </Button>
                </ModalFooter>
              </Modal>
            </form>
          );

        }}

      </Formik>



    </Container>
  );
}
